import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Card, ConfigProvider, InputNumber, Button, Popconfirm, Table, Radio, Select, Modal, Form, Tabs, Tag, Typography, theme } from 'antd';
import enGB from 'antd/locale/en_GB';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import PageHeader from '@/components/common/PageHeader';
import {
  useCalendars,
  useCreateCalendar,
  useCreateHoliday,
  useDeleteCalendar,
  useDeleteHoliday,
  useDeliverySettings,
  useHolidays,
  useSaveDeliverySettings,
  useUpdateCalendar,
  useUpdateHoliday,
} from '../hooks';
import { BankHolidayModal } from '../components/BankHolidayModal';
import { BufferDaysTab } from '../components/BufferDaysTab';
import { DEFAULT_BUFFER_DAYS } from '../constants';

const { Text } = Typography;

const currentYear = dayjs().year();
// A handful of past years plus a generous run of future years, so calendars
// can be set up well ahead of time.
const YEAR_OPTIONS = Array.from({ length: 50 }, (_, i) => currentYear - 2 + i).map((y) => ({
  value: y,
  label: String(y),
}));

export const DeliverySettingsPage: React.FC = () => {
  const { token } = theme.useToken();
  const { data: settings } = useDeliverySettings();
  const saveSettingsMutation = useSaveDeliverySettings();

  const { data: calendars = [], isLoading: isCalendarsLoading } = useCalendars();
  const createCalendarMutation = useCreateCalendar();
  const updateCalendarMutation = useUpdateCalendar();
  const deleteCalendarMutation = useDeleteCalendar();

  const [activeCalendarId, setActiveCalendarId] = useState<string | undefined>(undefined);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarModalMode, setCalendarModalMode] = useState<'create' | 'edit'>('create');
  const [calendarForm] = Form.useForm<{ year: number; bufferDays: number }>();

  const activeCalendar = calendars.find((c) => c.id === activeCalendarId);
  const bufferDays = settings?.bufferDays ?? DEFAULT_BUFFER_DAYS;

  // Seed the active calendar from saved settings once calendars have loaded.
  // Falls back to the calendar matching the current year, then the first
  // calendar in the list, if there's no (still-valid) saved selection.
  useEffect(() => {
    if (activeCalendarId || calendars.length === 0) return;
    const saved = settings?.activeCalendarId;
    if (saved && calendars.some((c) => c.id === saved)) {
      setActiveCalendarId(saved);
      return;
    }
    const currentYearCalendar = calendars.find((c) => c.name === String(currentYear));
    setActiveCalendarId(currentYearCalendar?.id ?? calendars[0]?.id);
  }, [settings, calendars, activeCalendarId]);

  const handleSelectCalendar = (id: string) => {
    setActiveCalendarId(id);
    saveSettingsMutation.mutate({ bufferDays, activeCalendarId: id });
  };

  const openCreateCalendarModal = () => {
    setCalendarModalMode('create');
    calendarForm.setFieldsValue({ year: currentYear, bufferDays });
    setCalendarModalOpen(true);
  };

  const openEditCalendarModal = () => {
    if (!activeCalendar) return;
    setCalendarModalMode('edit');
    const parsedYear = Number(activeCalendar.name);
    calendarForm.setFieldsValue({
      year: Number.isFinite(parsedYear) ? parsedYear : currentYear,
      bufferDays,
    });
    setCalendarModalOpen(true);
  };

  const handleSaveCalendarModal = async () => {
    const values = await calendarForm.validateFields();
    const name = String(values.year);
    const description = String(values.bufferDays);

    if (calendarModalMode === 'edit' && activeCalendarId) {
      updateCalendarMutation.mutate(
        { id: activeCalendarId, name, description },
        {
          onSuccess: () => {
            saveSettingsMutation.mutate({ bufferDays: values.bufferDays, activeCalendarId });
            setCalendarModalOpen(false);
          },
        },
      );
      return;
    }

    createCalendarMutation.mutate(
      { name, description },
      {
        onSuccess: (calendar) => {
          saveSettingsMutation.mutate({ bufferDays: values.bufferDays, activeCalendarId: calendar.id });
          setActiveCalendarId(calendar.id);
          setCalendarModalOpen(false);
        },
      },
    );
  };

  const { data: holidays = [], isLoading: isHolidaysLoading } = useHolidays(activeCalendarId);
  const createHolidayMutation = useCreateHoliday();
  const updateHolidayMutation = useUpdateHoliday();
  const deleteHolidayMutation = useDeleteHoliday();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState<'month' | 'year'>('month');

  // The widget's displayed year always tracks the selected calendar's name
  // (its year) — there is no separate year filter, only month navigation.
  const activeCalendarYear = Number(activeCalendar?.name);
  const lockedYear = Number.isFinite(activeCalendarYear) ? activeCalendarYear : currentYear;
  // Defaults to today's month/day with the locked year swapped in — when the
  // active calendar is the current year this lands on today (so the antd
  // Calendar's own "today" highlight is visible on load), otherwise it opens
  // on the same month in that other year rather than always January.
  const [calendarPanelDate, setCalendarPanelDate] = useState<Dayjs>(() => dayjs().year(lockedYear));

  useEffect(() => {
    setCalendarPanelDate((prev) => (prev.year() === lockedYear ? prev : dayjs().year(lockedYear)));
  }, [lockedYear]);

  const holidayByDate = useMemo(
    () => new Map(holidays.map((h) => [dayjs(h.scheduledStart).format('YYYY-MM-DD'), h])),
    [holidays],
  );

  const isPastHoliday = (holiday: { scheduledEnd: string }) =>
    dayjs(holiday.scheduledEnd).isBefore(dayjs(), 'day');

  const handleSelectDate = (date: Dayjs, { source }: { source: 'year' | 'month' | 'date' | 'customize' }) => {
    // Clicking a month in year view should switch into that month's date
    // view, not open the mark-holiday modal.
    if (source === 'month') {
      setCalendarPanelDate(date.year(lockedYear));
      setCalendarViewMode('month');
      return;
    }
    if (source !== 'date') return;
    setCalendarPanelDate(date.year(lockedYear));
    setSelectedDate(date);
    setHolidayModalOpen(true);
  };

  const existingForSelected = selectedDate
    ? holidayByDate.get(selectedDate.format('YYYY-MM-DD'))
    : undefined;

  const handleSaveHoliday = (name: string) => {
    if (!selectedDate || !activeCalendarId) return;
    const scheduledStart = selectedDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    const scheduledEnd = selectedDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss');

    if (existingForSelected) {
      updateHolidayMutation.mutate(
        {
          calendarId: activeCalendarId,
          calendarRuleId: existingForSelected.id,
          name,
          scheduledStart,
          scheduledEnd,
        },
        { onSuccess: () => setHolidayModalOpen(false) },
      );
      return;
    }

    createHolidayMutation.mutate(
      { calendarId: activeCalendarId, name, scheduledStart, scheduledEnd },
      { onSuccess: () => setHolidayModalOpen(false) },
    );
  };

  const handleRemoveHoliday = () => {
    if (!existingForSelected || !activeCalendarId) return;
    deleteHolidayMutation.mutate(
      { id: existingForSelected.id, calendarId: activeCalendarId },
      { onSuccess: () => setHolidayModalOpen(false) },
    );
  };

  const handleDeleteCalendar = () => {
    if (!activeCalendarId) return;
    const deletedId = activeCalendarId;
    deleteCalendarMutation.mutate(deletedId, {
      onSuccess: () => {
        const remaining = calendars.filter((c) => c.id !== deletedId);
        const currentYearCalendar = remaining.find((c) => c.name === String(currentYear));
        const nextId = currentYearCalendar?.id ?? remaining[0]?.id;
        setActiveCalendarId(nextId);
        saveSettingsMutation.mutate({ bufferDays, activeCalendarId: nextId });
        setCalendarModalOpen(false);
      },
    });
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <PageHeader
        title="Delivery Settings"
        subtitle="Configure buffer days and holiday calendars used to calculate the earliest available delivery date"
      />

      <Tabs
        className="mt-6"
        items={[
          {
            key: 'calendar',
            label: 'Calendar',
            children: (
              <>
      <Card loading={isCalendarsLoading}>
        <Text strong className="block mb-1">Calendar</Text>
        <Text type="secondary" className="text-xs block mb-3">
          Each calendar is a year. Mark holidays on the selected calendar — its holidays and buffer days block delivery-date selection.
        </Text>
        <div className="flex items-center gap-2">
          <Select
            placeholder="Select a year"
            value={activeCalendarId}
            onChange={handleSelectCalendar}
            style={{ width: 200 }}
            options={calendars.map((c) => ({ value: c.id, label: c.name }))}
            notFoundContent={calendars.length === 0 ? 'No calendars yet' : undefined}
          />
          <Button icon={<PlusOutlined />} onClick={openCreateCalendarModal}>
            New Calendar
          </Button>
          {activeCalendarId && (
            <Button icon={<EditOutlined />} onClick={openEditCalendarModal}>
              Edit
            </Button>
          )}
        </div>
        {activeCalendarId && (
          <Text type="secondary" className="text-xs block mt-3">
            Buffer: {bufferDays} working days
          </Text>
        )}
      </Card>

      <Card className="mt-6" title={activeCalendar ? `Holiday Calendar — ${activeCalendar.name}` : 'Holiday Calendar'}>
        {!activeCalendarId ? (
          <Text type="secondary">Create a calendar above to start marking holidays.</Text>
        ) : (
          <>
            <ConfigProvider locale={enGB}>
            <Calendar
              value={calendarPanelDate}
              mode={calendarViewMode}
              onPanelChange={(date, mode) => {
                setCalendarPanelDate(date.year(lockedYear));
                setCalendarViewMode(mode);
              }}
              onSelect={handleSelectDate}
              headerRender={({ value, type, onChange, onTypeChange }) => (
                <div className="flex items-center justify-end gap-2 pb-3">
                  <Select
                    size="small"
                    value={value.month()}
                    style={{ width: 100 }}
                    onChange={(month) => onChange(value.clone().month(month).year(lockedYear))}
                    options={Array.from({ length: 12 }, (_, m) => ({
                      value: m,
                      label: dayjs().month(m).format('MMM'),
                    }))}
                  />
                  <Radio.Group
                    size="small"
                    value={type}
                    onChange={(e) => onTypeChange(e.target.value)}
                  >
                    <Radio.Button value="month">Month</Radio.Button>
                    <Radio.Button value="year">Year</Radio.Button>
                  </Radio.Group>
                </div>
              )}
              cellRender={(current, info) => {
                if (info.type !== 'date') return null;
                const isToday = current.isSame(dayjs(), 'day');
                const holiday = holidayByDate.get(current.format('YYYY-MM-DD'));
                if (!holiday && !isToday) return null;
                const isPast = holiday ? isPastHoliday(holiday) : false;
                return (
                  <div style={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isToday && (
                      <span
                        style={{
                          display: 'block',
                          backgroundColor: '#52c41a',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: token.fontSizeSM,
                          lineHeight: `${token.controlHeightSM - 4}px`,
                          height: token.controlHeightSM - 4,
                          borderRadius: 999,
                          padding: '0 8px',
                          textAlign: 'center',
                        }}
                      >
                        Today
                      </span>
                    )}
                    {holiday && (
                      <span
                        title={isPast ? `${holiday.name} (completed)` : holiday.name}
                        style={{
                          display: 'block',
                          backgroundColor: isPast ? '#8c8c8c' : '#1677ff',
                          color: '#ffffff',
                          textDecoration: isPast ? 'line-through' : 'none',
                          fontSize: token.fontSizeSM,
                          lineHeight: `${token.controlHeightSM - 4}px`,
                          height: token.controlHeightSM - 4,
                          borderRadius: 999,
                          padding: '0 8px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {holiday.name}
                      </span>
                    )}
                  </div>
                );
              }}
            />
            </ConfigProvider>

            <div className="mt-6">
              <Text strong className="block mb-2">
                All Holidays ({holidays.length})
              </Text>
              <Table
                dataSource={holidays}
                rowKey="id"
                size="small"
                pagination={false}
                loading={isHolidaysLoading}
                locale={{ emptyText: 'No holidays configured yet' }}
                columns={[
                  {
                    title: 'Date',
                    key: 'date',
                    width: 220,
                    render: (_: unknown, record) => (
                      <div className="flex items-center gap-2">
                        <Tag color={isPastHoliday(record) ? 'default' : 'blue'}>
                          {dayjs(record.scheduledStart).format('DD/MM/YYYY')}
                        </Tag>
                        {isPastHoliday(record) ? (
                          <Tag color="default">Completed</Tag>
                        ) : (
                          <Tag color="green">Upcoming</Tag>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                    render: (name: string, record) => (
                      <span style={isPastHoliday(record) ? { color: token.colorTextDisabled, textDecoration: 'line-through' } : undefined}>
                        {name}
                      </span>
                    ),
                  },
                  {
                    title: '',
                    key: 'actions',
                    width: 60,
                    render: (_: unknown, record) => {
                      const isDeletingThis = deleteHolidayMutation.isPending && deleteHolidayMutation.variables?.id === record.id;
                      return (
                        <Popconfirm
                          title="Delete this holiday? This cannot be undone."
                          onConfirm={() => deleteHolidayMutation.mutate({ id: record.id, calendarId: record.calendarId })}
                          okText="Delete"
                          okButtonProps={{ danger: true, loading: isDeletingThis }}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} loading={isDeletingThis} />
                        </Popconfirm>
                      );
                    },
                  },
                ]}
              />
            </div>
          </>
        )}
      </Card>
              </>
            ),
          },
          {
            key: 'bufferDays',
            label: 'Buffer Days',
            children: <BufferDaysTab />,
          },
        ]}
      />

      <BankHolidayModal
        open={holidayModalOpen}
        date={selectedDate}
        existing={existingForSelected}
        onClose={() => setHolidayModalOpen(false)}
        onSave={handleSaveHoliday}
        onRemove={handleRemoveHoliday}
        isSaving={createHolidayMutation.isPending || updateHolidayMutation.isPending}
        isRemoving={deleteHolidayMutation.isPending}
      />

      <Modal
        title={calendarModalMode === 'edit' ? 'Edit Calendar' : 'New Calendar'}
        open={calendarModalOpen}
        onCancel={() => setCalendarModalOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCalendarModalOpen(false)}>Cancel</Button>
            {calendarModalMode === 'edit' && (
              <Popconfirm
                title="Delete this calendar and all its holidays? This cannot be undone."
                onConfirm={handleDeleteCalendar}
                okText="Delete"
                okButtonProps={{ danger: true, loading: deleteCalendarMutation.isPending }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteCalendarMutation.isPending}>Delete</Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              loading={createCalendarMutation.isPending || updateCalendarMutation.isPending}
              onClick={handleSaveCalendarModal}
            >
              Save
            </Button>
          </div>
        }
      >
        <Form form={calendarForm} layout="vertical">
          <Form.Item
            name="year"
            label="Year"
            rules={[{ required: true, message: 'Please select a year' }]}
          >
            <Select options={YEAR_OPTIONS} placeholder="Select year" />
          </Form.Item>
          <Form.Item
            name="bufferDays"
            label="Buffer Days"
            tooltip="Minimum number of working days (Mon-Fri, excluding calendar holidays) required before an order can be delivered"
            rules={[{ required: true, message: 'Please enter buffer days' }]}
          >
            <InputNumber min={0} max={60} addonAfter="working days" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliverySettingsPage;
